import Api, { FareUpdatedNotificationData, Notification, User } from '@silbo/core';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { liveQueryClient } from '../../../../live-query-client';
import toast from '../../../../toast';
import { AcknowledgeNotificationAction } from '../../components/AcknowledgeNotificationAction';
import FareUpdatedDifferences from '../../components/FareUpdatedDifferences';

const useFareUpdatedNotifications = (user?: User) => {
  const { t } = useTranslation('patient-transport');

  useEffect(() => {
    let subscription: Parse.LiveQuerySubscription | undefined;

    const init = async () => {
      const userSessionToken = Api.User.current()?.getSessionToken();
      if (!user || !userSessionToken) {
        return;
      }
      const handleCreate = async (notification: Notification) => {
        const data = notification.data as FareUpdatedNotificationData;

        toast.info({
          title: t('fareUpdatedToast.title', { reference: data.fare.reference }),
          message: <FareUpdatedDifferences differences={data.differences} />,
          id: `fare-updated-${notification.id}`,
          duration: Infinity,
          dismissible: false,
          action: <AcknowledgeNotificationAction notification={notification} />,
        });
      };

      subscription = liveQueryClient.subscribe(getFareUpdatedNotificationsQuery(user), userSessionToken);
      if (subscription) {
        subscription.on('create', handleCreate);
      }
    };

    init();

    return () => {
      if (subscription) {
        liveQueryClient.unsubscribe(subscription);
      }
    };
  }, [user]);
};

const getFareUpdatedNotificationsQuery = (user?: User) =>
  new Api.Query(Notification)
    .equalTo('triggerEvent', 'FARE_UPDATED')
    .equalTo('status', 'RUNNING')
    .equalTo('sendTo', user)
    .descending('createdAt');

export default useFareUpdatedNotifications;
